<template>
    <sidebar>
        <sidebar-header class="p-0">
            <div
                :class="{
                    'px-2': sidebar.unfoldable,
                    'px-4': !sidebar.unfoldable,
                }"
                class="sidebar-brand-wrapper d-flex align-items-center justify-content-center w-100"
            >
                <sidebar-brand
                    v-if="logoUrl"
                    class="d-flex align-items-center clickable"
                    @click.stop="$inertia.visit('/')"
                >
                    <div
                        class="d-flex justify-content-center align-items-center"
                    >
                        <img
                            :class="{
                                'sidebar-brand__full': !sidebar.unfoldable,
                                'sidebar-brand__narrow': sidebar.unfoldable,
                            }"
                            :src="logoUrl"
                            alt="App logo"
                        />
                    </div>
                </sidebar-brand>
                <sidebar-brand
                    v-else
                    class="d-flex align-items-center clickable w-100"
                    @click.stop="$inertia.visit('/')"
                >
                    <div
                        class="curie_logo d-flex justify-content-start align-items-center text-white"
                        style="padding: 12px 0 11px; margin-bottom: 49px"
                    >
                        <LogoCurie
                            v-if="!sidebar.unfoldable"
                            :height="38"
                            :width="144"
                            title="Curie Technologies Inc."
                        />
                        <LogoCurieCC
                            v-else
                            :height="32"
                            :width="34"
                            title="Curie Technologies Inc."
                        />
                    </div>
                </sidebar-brand>
            </div>
            <sidebar-toggler />
        </sidebar-header>
        <div class="d-flex flex-column justify-content-between flex-grow-1">
            <sidebar-nav>
                <sidebar-nav-item
                    v-if="
                        hasAnyPermission('asset.update') &&
                        $page.props.assets_stats.onboardingIncomplete > 0
                    "
                    :count="$page.props.assets_stats.onboardingIncomplete"
                    routeName="assets.activation"
                    ><template #icon><Tag /></template
                ></sidebar-nav-item>
                <sidebar-nav-item
                    v-if="
                        hasFeature('asset-work-order-management') &&
                        hasAnyPermission('workorder.read')
                    "
                    icon="cil-view-column"
                    routeName="workorders.index"
                />
                <sidebar-nav-item
                    v-if="
                        hasFeature('dashboard') &&
                        hasAnyPermission('dashboard.read')
                    "
                    icon="cil-bar-chart"
                    routeName="dashboard.index"
                />
                <sidebar-nav-item
                    v-if="hasAnyPermission('asset.read')"
                    :count="totalAssetsCount"
                    icon="cil-layers"
                    routeName="assets.index"
                >
                    <ul
                        :class="{
                            'd-none':
                                !$page.props.activeNavigation['assets.index'],
                        }"
                        class="list-unstyled sidebar-subnav"
                    >
                        <sidebar-sub-nav-item
                            :active="routeContainsUsageCategory('active')"
                            :href="
                                route('assets.index', {
                                    usages: ['active'],
                                })
                            "
                            class="d-block"
                        >
                            {{ trans('assets.sidebar.active') }} ({{
                                countAssetsByUsageCategory('active')
                            }})
                        </sidebar-sub-nav-item>
                        <sidebar-sub-nav-item
                            :active="routeContainsUsageCategory('spare')"
                            :href="
                                route('assets.index', {
                                    usages: ['spare'],
                                })
                            "
                            class="d-block"
                        >
                            {{ trans('assets.sidebar.spare') }} ({{
                                countAssetsByUsageCategory('spare')
                            }})
                        </sidebar-sub-nav-item>
                        <sidebar-sub-nav-item
                            :active="
                                routeContainsUsageCategory(['decommissioned'])
                            "
                            :href="
                                route('assets.index', {
                                    usages: ['decommissioned'],
                                })
                            "
                            class="d-block"
                        >
                            {{ trans('assets.sidebar.decommissioned') }} ({{
                                countAssetsByUsageCategory('decommissioned')
                            }})
                        </sidebar-sub-nav-item>
                    </ul>
                </sidebar-nav-item>

                <li v-if="showReporting" class="nav-item">
                    <a
                        :class="{
                            'c-active': isReportingActive,
                        }"
                        :title="trans('navigation.reporting')"
                        class="nav-link"
                        href="javascript:void(0)"
                        @click.prevent="
                            showReportingSubNav = !showReportingSubNav
                        "
                    >
                        <icon class="nav-icon" name="cil-description" />
                        {{ trans('navigation.reporting') }}
                    </a>
                    <collapse :show="isReportingActive || showReportingSubNav">
                        <ul class="list-unstyled sidebar-subnav">
                            <sidebar-sub-nav-item
                                v-if="showInoperableAssets"
                                :active="
                                    route().current() ===
                                    'reporting-light.index'
                                "
                                :href="route('reporting-light.index')"
                                class="d-block"
                            >
                                {{
                                    trans(
                                        'navigation.reporting.inoperable_assets',
                                    )
                                }}
                            </sidebar-sub-nav-item>
                            <sidebar-sub-nav-item
                                v-if="showTestRecords"
                                :active="route().current() === 'tests.index'"
                                :href="route('tests.index')"
                            >
                                {{ trans('navigation.reporting.tests') }}
                            </sidebar-sub-nav-item>
                            <sidebar-sub-nav-item
                                v-if="hasAnyPermission('activity.read')"
                                :active="
                                    route().current() === 'activities.index'
                                "
                                :href="route('activities.index')"
                            >
                                {{ trans('navigation.reporting.activities') }}
                            </sidebar-sub-nav-item>
                        </ul>
                    </collapse>
                </li>
                <sidebar-nav-title
                    v-if="
                        hasAnyPermission([
                            'user.index',
                            'location.index',
                            'settings.index',
                        ])
                    "
                    >{{ trans('navigation.settings') }}
                </sidebar-nav-title>
                <sidebar-nav-item
                    v-if="hasAnyPermission('user.index')"
                    icon="cil-group"
                    routeName="users.index"
                />
                <sidebar-nav-item
                    v-if="hasAnyPermission('location.index')"
                    icon="cil-airplane-mode"
                    routeName="locations.index"
                />
                <sidebar-nav-item
                    v-if="
                        hasAnyPermission([
                            'owner.index',
                            'operator.index',
                            'maintainer.index',
                        ])
                    "
                    icon="cil-sitemap"
                    routeName="stakeholders.index"
                />
                <sidebar-nav-item
                    v-if="hasAnyPermission('settings.index')"
                    icon="cil-cog"
                    routeName="settings.index"
                />
                <li
                    :title="trans('navigation.support')"
                    class="nav-item"
                    style="margin-top: 2rem"
                >
                    <a
                        :href="supportLink"
                        class="nav-link d-flex justify-content-between align-items-center"
                        target="_blank"
                    >
                        <span
                            class="d-flex nav-icon hidden-wide align-items-center"
                            style="line-height: 1.25rem"
                            >?</span
                        >
                        <span
                            class="d-flex justify-content-between align-items-center w-100"
                            >{{ trans('navigation.support') }}
                            <icon name="cil-external-link"
                        /></span>
                    </a>
                </li>
            </sidebar-nav>
            <div
                class="d-flex flex-column align-items-center py-3 hidden-narrow"
            >
                <optional-link :href="curieTechUrl" class="text-white">
                    <LogoCurie :height="15" title="Curie Technologies Inc." />
                </optional-link>
                <div v-if="version" class="px-4 version text-center">
                    <small :title="commit" class="text-muted"
                        ><b>v{{ version }}</b></small
                    >
                </div>
            </div>
        </div>
    </sidebar>
    <div
        v-if="sidebar.visible"
        :class="{ show: sidebar.visible }"
        class="fade sidebar-backdrop"
        @click="sidebar.close"
    />
</template>

<script setup>
import SidebarSubNavItem from '@/Components/SidebarSubNavItem.vue';
import OptionalLink from '@/Components/OptionalLink.vue';
import Sidebar from '@/Components/Sidebar.vue';
import SidebarNavItem from '@/Components/SidebarNavItem.vue';
import SidebarNav from '@/Components/SidebarNav.vue';
import SidebarNavTitle from '@/Components/SidebarNavTitle.vue';
import SidebarBrand from '@/Components/SidebarBrand.vue';
import SidebarToggler from '@/Components/SidebarToggler.vue';
import SidebarHeader from '@/Components/SidebarHeader.vue';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useSidebarStore } from '@/stores/sidebar.js';
import { router, usePage } from '@inertiajs/vue3';
import { isEqual } from 'lodash';
import Collapse from '@/Components/Collapse.vue';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import LogoCurie from '../../icons/LogoCurie.vue';
import Tag from '../../icons/Tag.vue';
import LogoCurieCC from '../../icons/LogoCurieCC.vue';

const sidebar = useSidebarStore();
const showReportingSubNav = ref(false);
const showReporting = computed(
    () => showInoperableAssets || showActivities || showTestRecords,
);
const isReportingActive = computed(() => {
    return (
        route().current()?.startsWith('reporting-light.') ||
        route().current()?.startsWith('activities.') ||
        route().current()?.startsWith('tests.')
    );
});
const showInoperableAssets = computed(
    () =>
        hasFeature('reporting-light') &&
        hasAnyPermission('reporting_light.read'),
);

const showActivities = computed(() => hasAnyPermission('activities.read'));
const showTestRecords = computed(
    () => hasAnyPermission('test.overview') && hasFeature('test_record'),
);

const $page = usePage();
const supportLink = computed(() => $page.props.supportLink);
const userManual = computed(() => `user_manual_${$page.props.locale}.pdf`);
const logoUrl = computed(() => $page.props.settings.big_logo_url);
const version = computed(() => $page.props.version?.semver);
const commit = computed(() => $page.props.version?.commit_hash);
const totalAssetsCount = computed(() =>
    Object.keys($page.props.assets_stats)
        .filter((key) => key !== 'onboardingIncomplete')
        .reduce((a, b) => a + $page.props.assets_stats[b], 0),
);
const curieTechUrl = computed(() => $page.props.curie_tech_url);

// methods: {
function countAssetsByUsageCategory(usageCategory) {
    return $page.props.assets_stats[usageCategory] || 0;
}

function routeContainsUsageCategory(usageCategory) {
    if (route().current() !== 'assets.index') {
        return false;
    }

    const params = route().params;

    // Wrap in array if necessary
    usageCategory = Array.isArray(usageCategory)
        ? usageCategory
        : [usageCategory];

    return isEqual(params.usages, usageCategory);
}

function closeSidebarOnSmallScreens() {
    if (document.body.clientWidth < 992) {
        sidebar.close();
    }
}

let removeEventListener;

onMounted(() => {
    removeEventListener = router.on('start', closeSidebarOnSmallScreens);
});

onUnmounted(() => {
    removeEventListener && removeEventListener();
});
</script>
<style scoped>
.curie_logo__narrow {
    overflow: hidden;
    width: 38px;
}
</style>
